import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    useMediaQuery,
    Box,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import { useDispatch, useSelector } from 'react-redux';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import { CustomButton } from '../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../layout/styledcomponents/CustomIconButton';
import { CustomBadge } from '../../layout/styledcomponents/CustomBadge';
import {
    selectExperienceFilters,
    setExperienceFilters,
} from '../../app/Slices/ExperienceFiltersSlice';
import { resetExperienceSearchFilter } from '../../app/Slices/ExperienceSearchSlice';
import FormMultiAutocomplete from './FormMultiAutocomplete';
import FormSelect from './FormSelect';
import CustomInputLabel from '../../layout/styledcomponents/CustomInputLabel';
import { trackEvent } from '../../app/Analytics';
import UserRole from '../../shared/AuthRoles';

const NUMBER_OPTIONS = [
    { value: 'any', label: 'Any' },
    { value: '1', label: '1+' },
    { value: '2', label: '2+' },
    { value: '3', label: '3+' },
    { value: '4', label: '4+' },
    { value: '5', label: '5+' },
    { value: '6', label: '6+' },
    { value: '7', label: '7+' },
    { value: '8', label: '8+' },
    { value: '9', label: '9+' },
    { value: '10', label: '10+' },
];

const VISIT_TYPE_OPTIONS = [
    { value: 'any', label: 'Any' },
    { value: 'personal', label: 'Personal' },
    { value: 'business', label: 'Business' },
];

const AMENITIES_OPTIONS = [
    { value: 'aircon', label: 'Aircon' },
    { value: 'bbq', label: 'BBQ' },
    { value: 'boardroom', label: 'Boardroom' },
    { value: 'dedicatedWorkspace', label: 'Dedicated Workspace' },
    { value: 'gamesRoom', label: 'Games Room' },
    { value: 'kitchen', label: 'Kitchen' },
    { value: 'washingMachine', label: 'Laundry' },
    { value: 'massages', label: 'Massages' },
    { value: 'parking', label: 'Parking' },
    { value: 'pool', label: 'Pool' },
    { value: 'spa', label: 'Spa' },
    { value: 'tv', label: 'TV' },
    { value: 'waterSports', label: 'Water Sport' },
    { value: 'wifi', label: 'Wifi' },
    { value: 'wineCellar', label: 'Wine Cellar' },
];

const FiltersFormSchema = z.object({
    guests: z.string(),
    overnightGuests: z.string(),
    bedrooms: z.string(),
    visitType: z.array(z.string()),
    amenities: z.array(z.string()),
    rating: z.string(),
});

type FiltersFormValues = z.infer<typeof FiltersFormSchema>;

function FilterRow({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <Grid
            container
            alignItems='center'
            spacing={1.5}
            sx={{
                mb: 2,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
        >
            <Grid item xs={12} sm={5} md={4}>
                <CustomInputLabel fontSize='14px' fontWeight='500'>
                    {label}
                </CustomInputLabel>
            </Grid>
            <Grid item xs={12} sm={7} md={8}>
                {children}
            </Grid>
        </Grid>
    );
}

interface FiltersModalProps {
    management?: boolean;
    /** When provided, the dialog open state is controlled by the parent. */
    open?: boolean;
    /** Called when the dialog wants to change open state. */
    onOpenChange?: (open: boolean) => void;
    /** Hide the built-in trigger button (use a parent element to open). */
    hideTrigger?: boolean;
}

export default function FiltersModal({
    management,
    open: controlledOpen,
    onOpenChange,
    hideTrigger = false,
}: FiltersModalProps) {
    const dispatch = useDispatch();
    const filters = useSelector(selectExperienceFilters);
    const isSmallScreen = useMediaQuery('(max-width:600px)');
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const openModal = isControlled ? !!controlledOpen : internalOpen;
    const loggedInUser = UserRole();
    const setOpenModal = useCallback(
        (next: boolean) => {
            if (isControlled) {
                onOpenChange?.(next);
            } else {
                setInternalOpen(next);
            }
        },
        [isControlled, onOpenChange],
    );

    const toFormDefaults = useCallback(
        (f: ReturnType<typeof selectExperienceFilters>): FiltersFormValues => ({
            guests: f.guests ? String(f.guests) : 'any',
            overnightGuests: f.overnightGuests
                ? String(f.overnightGuests)
                : 'any',
            bedrooms: f.bedrooms ? String(f.bedrooms) : 'any',
            rating: f.rating ? String(f.rating) : 'any',
            visitType: [
                f.visitType && f.visitType !== '' ? f.visitType : 'any',
            ],
            amenities: f.amenities || [],
        }),
        [],
    );

    const toReduxPayload = useCallback((v: FiltersFormValues) => {
        return {
            guests: v.guests === 'any' ? '' : v.guests,
            overnightGuests:
                v.overnightGuests === 'any' ? '' : v.overnightGuests,
            bedrooms: v.bedrooms === 'any' ? '' : v.bedrooms,
            rating: v.rating === 'any' ? '' : v.rating,
            visitType:
                v.visitType && v.visitType[0] && v.visitType[0] !== 'any'
                    ? v.visitType[0]
                    : '',
            amenities: v.amenities,
        };
    }, []);

    const methods = useForm<FiltersFormValues>({
        resolver: zodResolver(FiltersFormSchema),
        defaultValues: toFormDefaults(filters),
    });

    const { control, reset, getValues } = methods;

    useEffect(() => {
        trackEvent(
            'Experiences',
            'Exp Filters Clicked',
            `Exp Filters Clicked - (${loggedInUser?.role})`,
        );
    }, []);

    const filterCount = useMemo(() => {
        return (
            (filters.guests ? 1 : 0) +
            (filters.overnightGuests ? 1 : 0) +
            (filters.bedrooms ? 1 : 0) +
            (filters.rating ? 1 : 0) +
            (filters.visitType ? 1 : 0) +
            (filters.amenities.length ? 1 : 0)
        );
    }, [filters]);

    const onClickClearFilters = useCallback(() => {
        reset({
            guests: 'any',
            overnightGuests: 'any',
            bedrooms: 'any',
            rating: 'any',
            visitType: ['any'],
            amenities: [],
        });
        dispatch(resetExperienceSearchFilter());
    }, [reset]);

    const handleSaveAndClose = useCallback(() => {
        const current = getValues();
        dispatch(
            setExperienceFilters({
                ...filters,
                ...toReduxPayload(current),
            }),
        );
        setOpenModal(false);
    }, [dispatch, filters, getValues, toReduxPayload]);

    const handleOpen = () => {
        reset(toFormDefaults(filters));
        setOpenModal(true);
    };

    const handleCloseIcon = () => handleSaveAndClose();
    const theme = useTheme();

    useEffect(() => {
        if (openModal) {
            reset(toFormDefaults(filters));
        }
    }, [openModal, filters, reset, toFormDefaults]);

    return (
        <>
            {!hideTrigger && (
                <CustomBadge
                    color='green'
                    badgeContent={filterCount}
                    invisible={filterCount === 0}
                >
                    <CustomIconButton
                        onClick={handleOpen}
                        disableRipple
                        disableTouchRipple
                        sx={{
                            p: 0.5,
                            minWidth: 'auto',
                            borderRadius: 1,
                        }}
                    >
                        <TuneIcon fontSize='small' />
                    </CustomIconButton>
                </CustomBadge>
            )}
            <Dialog
                open={openModal}
                onClose={handleSaveAndClose}
                maxWidth='sm'
                fullWidth
                fullScreen={isSmallScreen}
                PaperProps={{
                    sx: {
                        borderRadius: { xs: '16px 16px 0 0', sm: 4 },
                        width: { xs: '100%', sm: 500 },
                        maxHeight: { xs: 'calc(100vh - 60px)', sm: '90vh' },
                        position: { xs: 'absolute', sm: 'relative' },
                        bottom: { xs: 0, sm: 'auto' },
                        left: 0,
                        right: 0,
                        mx: 'auto',
                        backgroundColor: theme.palette.neutral.main,
                    },
                }}
                sx={{
                    '& .MuiDialog-container': {
                        alignItems: { xs: 'flex-end', sm: 'center' },
                    },
                }}
            >
                <DialogTitle>
                    Filters
                    <CustomIconButton
                        onClick={handleCloseIcon}
                        sx={{
                            color: 'custom.grey',
                            '&:hover': {
                                cursor: 'pointer',
                                color: 'primary.main',
                                backgroundColor: 'transparent',
                            },
                        }}
                    >
                        <CloseIcon
                            sx={{
                                fontSize: '1.5rem',
                                color: 'text.primary',
                                '&:hover': {
                                    color: 'highlight.main',
                                },
                            }}
                        />
                    </CustomIconButton>
                </DialogTitle>

                <DialogContent>
                    <FormProvider {...methods}>
                        <Box component='form' noValidate>
                            <FilterRow label='No Guests:'>
                                <FormSelect
                                    name='guests'
                                    control={control}
                                    options={NUMBER_OPTIONS}
                                    fontSize='14px'
                                />
                            </FilterRow>

                            <FilterRow label='No Overnight Guests:'>
                                <FormSelect
                                    name='overnightGuests'
                                    control={control}
                                    options={NUMBER_OPTIONS}
                                    fontSize='14px'
                                />
                            </FilterRow>

                            <FilterRow label='No Bedrooms:'>
                                <FormSelect
                                    name='bedrooms'
                                    control={control}
                                    options={NUMBER_OPTIONS}
                                    fontSize='14px'
                                />
                            </FilterRow>

                            {management && (
                                <FilterRow label='Visit Type:'>
                                    <FormMultiAutocomplete
                                        name='visitType'
                                        control={control}
                                        options={VISIT_TYPE_OPTIONS}
                                        emptyPlaceholder='Any'
                                        fontSize='14px'
                                        getOptionValue={(opt) => opt.value}
                                        getOptionLabel={(opt) => opt.label}
                                    />
                                </FilterRow>
                            )}

                            <FilterRow label='Amenities:'>
                                <FormMultiAutocomplete
                                    name='amenities'
                                    control={control}
                                    options={AMENITIES_OPTIONS}
                                    emptyPlaceholder='Any'
                                    fontSize='14px'
                                    getOptionValue={(opt) => opt.value}
                                    getOptionLabel={(opt) => opt.label}
                                />
                            </FilterRow>
                            <FilterRow label='Rating'>
                                <FormSelect
                                    name='rating'
                                    control={control}
                                    options={[
                                        { value: 'any', label: 'Any' },
                                        { value: '2', label: '2+' },
                                        { value: '3', label: '3+' },
                                        { value: '4', label: '4+' },
                                    ]}
                                    fontSize='14px'
                                />
                            </FilterRow>
                        </Box>
                    </FormProvider>
                </DialogContent>

                <DialogActions
                    sx={{
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: 'space-between',
                    }}
                >
                    <CustomButton
                        variant='text'
                        color='gray'
                        onClick={onClickClearFilters}
                        sx={{
                            fontSize: '12px',
                            textTransform: 'none',
                        }}
                    >
                        Clear Filters
                    </CustomButton>

                    <CustomButton
                        variant='contained'
                        color='primary'
                        onClick={handleSaveAndClose}
                        sx={{ textTransform: 'none' }}
                    >
                        Show Experiences
                    </CustomButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
